import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCycleCountStore } from "../store/cycle-count";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonRow,
  IonText,
} from "@ionic/react";
import { maxResults } from "../hooks/useQueryExec";
import Fuse from "fuse.js";
import CardFilter from "./CardFilter";
import { encodeParam } from "../util/ApiHelper";

/**
 * Offline read surface for downloaded cycle-count batches, shown on the Home
 * screen's offline tabs. Mirrors DownloadedWorkOrders / DownloadedEquipments:
 * reads from the persisted store and filters locally (no network).
 */
export default function DownloadedCycleCounts() {
  const downloadedBatches = useCycleCountStore(
    (state) => state.downloadedBatches
  );
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);
  const [searchText, setSearchText] = useState("");

  const [filteredRows, setFilteredRows] = useState(downloadedBatches);

  useEffect(() => {
    setFilteredRows(downloadedBatches);
  }, [downloadedBatches]);

  const hasDownloaded = downloadedBatches.length > 0;

  const fuse = useMemo(
    () =>
      new Fuse(downloadedBatches, {
        keys: ["name_0", "batch_id_0", "stores_location_0", "status_0"],
        includeScore: true,
        minMatchCharLength: 2,
        threshold: 0.5,
      }),
    [downloadedBatches]
  );

  const handleFilter = useCallback(
    async (queryString: string) => {
      if (queryString) {
        const res = fuse.search(queryString);
        setFilteredRows(res.map((r) => r.item).slice(0, maxResults));
      } else {
        setFilteredRows(downloadedBatches.slice(0, maxResults));
      }
      setSearchText(queryString);
    },
    [downloadedBatches, fuse]
  );

  return (
    <>
      <CardFilter
        hideFilter={downloadedBatches.length <= 0}
        queryRows={[
          {
            Columns: [
              {
                Hidden: false,
                Name: "Name",
                Order: 1,
                Text: "Text",
                Type: "",
                Value: "",
              },
            ],
            HeaderColor: "",
            HeaderName: "",
            HeaderRow: 0,
            HeaderText: "",
            HeaderType: "",
            HeaderValue: "",
          },
        ]}
        resultsFilter={searchText}
        filterMethod={handleFilter}
      />
      {hasDownloaded &&
        filteredRows.map((batch, index) => (
          <IonCard
            key={`downloaded-cc-${batch.batch_id_0}-${index}`}
            routerLink={`/CycleCount/${encodeParam(
              String(batch.batch_id_0)
            )}`}
          >
            <IonCardHeader>
              <IonCardTitle>{batch.name_0}</IonCardTitle>
              <IonCardSubtitle>{batch.status_0}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid>
                <IonRow hidden={!batch.batch_id_0}>
                  <IonCol>
                    {translations["lbl_incyclecountbatch_batchid"] ||
                      "Batch Id"}
                  </IonCol>
                  <IonCol>{batch.batch_id_0}</IonCol>
                </IonRow>
                <IonRow hidden={!batch.stores_location_0}>
                  <IonCol>
                    {translations[
                      "lbl_eqlocationsinquiryview_storeslocation"
                    ] || "Stores Location"}
                  </IonCol>
                  <IonCol>{batch.stores_location_0}</IonCol>
                </IonRow>
                <IonRow hidden={!batch.total_items_222}>
                  <IonCol>
                    {translations["lbl_invcyclecountcomputed_totalitems"] ||
                      "Total Items"}
                  </IonCol>
                  <IonCol>{batch.total_items_222}</IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>
        ))}

      <IonText
        hidden={hasDownloaded}
        color={"default"}
        style={{ padding: "16px", display: "inline-block" }}
      >
        {translatedMessages["NoResultsFound"]?.MessageText ||
          "No results found."}
      </IonText>
    </>
  );
}
